import t from 'react-translate';
import { useContext, useEffect, useState } from 'react';
import { AngularServicesContext } from 'react-app';
import MentoringProgramContext from 'athena/components/mentoring-program/context';

import NvModal, { ModalType } from 'shared/components/nv-modal';
import AuthenticateIntegration from './authenticate-integration';
import { camelCase } from 'lodash';
import {
  AuthenticationStatus,
  IntegrationAuth,
  mappingIntegrationType,
  OauthIntegration,
} from 'redux/schemas/models/mentoring-program-sessions';
import { getAuthenticatedAccounts, getOAuthLink } from '../utils';
import { IntegrationTypes, SessionTypes } from 'redux/schemas/models/live-event';
import { AuthenticationView, LocalStorageKeys } from '../..';
import { css } from '@emotion/react';
import { doubleSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import moment from 'moment';
import useCalendarIntegration, { CalendarIntegrationSettings } from 'athena/hooks/use-calendar-integration';

const modalStyles = css`
  .bs4-modal-body {
    gap: ${standardSpacing}px;
    display: flex;
    flex-direction: column;
    padding: ${doubleSpacing}px ${standardSpacing}px;
  }
}`;


const getCleanedUrl = ($location: any) => {
  const currentUrl = `${$location.protocol()}://${$location.host()}/#!${$location.path()}${$location.hash()}`;
  const newUrl = currentUrl.replace(/([?&])authenticationView=\d+/, '$1');
  const cleanedUrl = newUrl.replace(/([?&])showSession=\d+/, '$1');
  const url = cleanedUrl.endsWith('?') || cleanedUrl.endsWith('&') ? cleanedUrl.slice(0, -1) : cleanedUrl;
  return url;
};

const getModalParams = ({
  integrations,
  sessionId,
  createMeetingPlan,
  calendarIntegration,
} : {
  integrations: IntegrationAuth,
  sessionId: number,
  createMeetingPlan: boolean,
  calendarIntegration: CalendarIntegrationSettings,
}) => {
  const { source = [] } = integrations || {};
  const { hasMs365CalendarIntegration, isAuthenticated } = calendarIntegration;
  const showSessionId = sessionId ? `&showSession=${sessionId}` : '';

  const connectedAccounts = getAuthenticatedAccounts(integrations).length + (isAuthenticated ? 1 : 0);
  const availableIntegrations = source.length + (hasMs365CalendarIntegration ? 1 : 0);
  const remainingIntegrations = availableIntegrations - connectedAccounts - 1;
  const showIntegrationsModal = remainingIntegrations > 0;

  let view = showIntegrationsModal
    ? AuthenticationView.SHOW_INTEGRATIONS
    : AuthenticationView.SHOW_SESSION;

  if (createMeetingPlan) {
    view = showIntegrationsModal
      ? AuthenticationView.SHOW_MEETING_PLAN_INTEGRATIONS
      : AuthenticationView.CREATE_MEETING_PLAN;
  }

  return `?authenticationView=${view}${showSessionId}`;
};

const sessionTypeMap: { [key: string]: string | null } = {
  [mappingIntegrationType.ZoomLiveSession]: SessionTypes.ZOOM,
  [mappingIntegrationType.TeamsLiveSession]: SessionTypes.MS_TEAMS,
  [mappingIntegrationType.CalendarLiveSession]: SessionTypes.MS_CALENDAR,
};

type EnableIntegrationsModalProps = {
  session: any;
  sessionId: number;
  integrations: any;
  createMeetingPlan: boolean;
  onClose: () => void;
};

const EnableIntegrationsModal = (props: EnableIntegrationsModalProps) => {
  const { session, sessionId, integrations, createMeetingPlan, onClose } = props;
  const { source = [] } = integrations || {};
  const { $location } = useContext(AngularServicesContext);
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { id, mentorshipProgramEnrollment: enrollmentId } = mentoringProgram;
  const [isMSCalendarAuthenticated, setIsMSCalendarAuthenticated] = useState(false);
  const [calendarIntegrationEmail, setCalendarIntegrationEmail] = useState<string | null>(null);

  const calendarIntegration = useCalendarIntegration(source);
  const { hasMs365CalendarIntegration, calendarIntegrationSettings } = calendarIntegration;

  const translationsBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.ENABLE_INTEGRATIONS_MODAL;

  const handleClick = (integration: string) => {
    const sessionType = (sessionTypeMap[integration] || null) as SessionTypes | null;
    const showModalParams = getModalParams({
      integrations,
      sessionId,
      createMeetingPlan,
      calendarIntegration,
    });
    const url = getCleanedUrl($location);
    const refererUrl = `${url}${showModalParams}`;
    const href = getOAuthLink({
      mentoringProgramId: id,
      sessionType,
      enrollmentId,
      refererUrl,
    });
    localStorage.setItem(LocalStorageKeys.redirection, 'true');
    if (session) {
      localStorage.setItem(LocalStorageKeys.session, JSON.stringify(session));
    }
    window.location.href = href;
  };

  const sections = source?.map((type: any) => {
    const camelType = camelCase(type);
    const integration: OauthIntegration = integrations[camelType];
    const isAuthenticated = integration.authenticatedStatus === AuthenticationStatus.AUTHENTICATED;
    const authenticationEmail = type !== IntegrationTypes.MS_TEAMS_USER_LEVEL
      ? integration.authenticationEmail : null;

    return (
      <div key={type} className='d-flex flex-column'>
        <AuthenticateIntegration
          integration={type}
          isAuthenticated={isAuthenticated}
          authenticationEmail={authenticationEmail}
          onClick={handleClick}
        />
      </div>
    );
  });

  if (hasMs365CalendarIntegration) {
    const msCalendarIntegration = (
      <div key='ms365' className='d-flex flex-column'>
        <AuthenticateIntegration
          integration={IntegrationTypes.MS_CALENDAR_USER_LEVEL}
          isAuthenticated={isMSCalendarAuthenticated}
          authenticationEmail={isMSCalendarAuthenticated ? calendarIntegrationEmail : null}
          onClick={handleClick}
        />
      </div>
    );
    const zoomIndex = source.indexOf(IntegrationTypes.ZOOM_USER_LEVEL);
    if (zoomIndex !== -1) {
      sections.splice(zoomIndex, 0, msCalendarIntegration);
    } else {
      sections.push(msCalendarIntegration);
    }
  }

  useEffect(() => {
    if (hasMs365CalendarIntegration && calendarIntegrationSettings) {
      let isAuthenticated = false;
      if (calendarIntegrationSettings) {
        const expiresAt = moment(calendarIntegrationSettings.expiresAt);
        isAuthenticated = moment().isBefore(expiresAt);
        setCalendarIntegrationEmail(calendarIntegrationSettings.email);
      }
      setIsMSCalendarAuthenticated(isAuthenticated);
    }
  }, [hasMs365CalendarIntegration, calendarIntegrationSettings]);

  return (
    <NvModal
      modalStyles={modalStyles}
      type={ModalType.FIXED}
      doubleModal
      header={translationsBase.TITLE()}
      headerClassName='page-title'
      body={sections}
      height='unset'
      onClose={onClose}
    />
  );
};

export default EnableIntegrationsModal;
